// MARK: NPM Modules
import React, { useState } from "react";
import styled from "styled-components";
// MARK: Redux
import { RootState } from "reduxFunctionality/index";
import { useSelector } from "react-redux";
// MARK: Types
import ReduxStyleguide from "types/redux/styleguide";
// MARK: Components
import Typography from "components/common/typography";
import Link from "components/common/svgs/link";
// MARK: Functionality
import { TrackGoogleAnalyticsEvent } from "utils/google-analytics";
import BlogCategory from "utils/google-analytics/events/blog";
import UserClickedCopyBlogLink from "utils/google-analytics/events/blog/userClickedCopyBlogLink";
// MARK: Styled Components

interface TitleContaienrProps {
  marginTop: number;
  maxWidth: number;
}

const TitleContainer = styled.div<TitleContaienrProps>`
  cursor: pointer !important;
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  margin-top: ${({ marginTop }) => marginTop + "px"};
  max-width: ${({ maxWidth }) => maxWidth + "px"};
  margin-left: auto;
  margin-right: auto;
`;

interface ElementContainerProps {
  opacity: number;
  position: string;
  top: number;
  right: number;
  marginRight: number;
  dimension: number;
}

const ElementContainer = styled.div<ElementContainerProps>`
  cursor: pointer !important;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ opacity }) => opacity};

  position: ${({ position }) => position};
  top: ${({ top }) => (top === 0 ? "" : top + "px")};
  left: ${({ right }) => (right !== 0 ? -right + "px" : "")};
  margin-right: ${({ marginRight }) =>
    marginRight !== 0 ? marginRight + "px" : ""};
  width: ${({ dimension }) => dimension + "px"};
  height: ${({ dimension }) => dimension + "px"};
  border-radius: ${({ dimension }) => dimension / 2 + "px"};
  transition: 0.6s cubic-bezier(0.65, 0, 0.35, 1);
`;

interface LinkContainerProps {
  dimension: number;
  backgroundColor: string;
}

const LinkContainer = styled.div<LinkContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ dimension }) => dimension + "px"};
  height: ${({ dimension }) => dimension + "px"};
  border-radius: ${({ dimension }) => dimension / 2 + "px"};
  background-color: ${({ backgroundColor }) => backgroundColor};
`;

// MARK: React Component
// Props
interface Props {
  id: string;
  className: string;
  color: string;
  background: string;
  headerType: string;
  copy: string;
  layoutWidth: number;
  onClick: () => void;
}
// Component
const BlogPostHeader = ({
  id,
  className,
  color,
  background,
  headerType,
  copy,
  layoutWidth,
  onClick,
}: Props) => {
  // Mark: React Redux Variables
  const styleguide: ReduxStyleguide = useSelector(
    (state: RootState) => state.styleguide
  );
  // MARK: State Variables
  const [isHoveringOver, setIsHoveringOver] = useState<boolean>(false);
  const [isHoveringOverLink, setIsHoveringOverLink] = useState<boolean>(false);
  // MARK: Variables
  const { margin, gutter, innerWidth, mobile } = styleguide;
  const label = "Click to copy the URL for this header.";

  // MARK: Functionality

  const renderElement = () => {
    var position: string;
    var right: number = 0;
    var top: number = 0;
    var marginRight: number = 0;
    const dimension: number = 48;
    switch (layoutWidth + 3 * margin < innerWidth) {
      case true:
        position = "absolute";
        right = dimension + gutter;
        top = headerType === "h4" ? -3 : 6;
        break;
      case false:
        position = "relative";
        marginRight = gutter;
        top = 6;
        break;
    }

    return (
      <ElementContainer
        title={label}
        aria-label={label}
        opacity={mobile ? 1 : isHoveringOver ? 1 : 0}
        position={position}
        marginRight={marginRight}
        right={right}
        top={top}
        dimension={dimension}
        onClick={() => {
          var baseUrl: string = process.env.GATSBY_BASE_URL as string;
          baseUrl = baseUrl.substring(0, baseUrl.length - 1);

          var pathName = window.location.pathname;
          if (
            pathName.substring(pathName.length - 1, pathName.length) !== "/"
          ) {
            pathName = pathName + "/";
          }

          const itemToCopy = baseUrl + pathName + "#" + id;
          // Copy to Clipboard
          navigator.clipboard.writeText(itemToCopy);
          // Send GA Event
          TrackGoogleAnalyticsEvent(
            BlogCategory,
            UserClickedCopyBlogLink,
            itemToCopy
          );
          // Send Callback
          onClick();
        }}
        onMouseEnter={() => {
          setIsHoveringOverLink(true);
        }}
        onMouseLeave={() => {
          setIsHoveringOverLink(false);
        }}
        onTouchStart={() => {
          setIsHoveringOverLink(true);
        }}
        onTouchEnd={() => {
          setIsHoveringOverLink(false);
        }}
        onTouchCancel={() => {
          setIsHoveringOverLink(false);
        }}
      >
        <LinkContainer
          dimension={margin}
          backgroundColor={isHoveringOverLink ? color : background}
        >
          <Link
            dimension={margin}
            color={isHoveringOverLink ? background : color}
            mobile={mobile}
          />
        </LinkContainer>
      </ElementContainer>
    );
  };

  const scrollToId = () => {
    const div = window.document.getElementById(id);

    if (div) {
      const rect = div.getBoundingClientRect();
      const offset = rect.y + window.scrollY - 200;
      scrollTo({
        top: offset,
        left: 0,
        behavior: "smooth",
      });
    }
  };

  // MARK: Render

  return (
    <TitleContainer
      onMouseEnter={() => {
        setIsHoveringOver(true);
      }}
      onMouseLeave={() => {
        setIsHoveringOver(false);
      }}
      onTouchStart={() => {
        setIsHoveringOver(true);
      }}
      onTouchEnd={() => {
        setIsHoveringOver(false);
      }}
      onTouchCancel={() => {
        setIsHoveringOver(false);
      }}
      marginTop={margin}
      maxWidth={layoutWidth}
    >
      {renderElement()}
      <Typography
        pointer={"cursor"}
        maxwidth={layoutWidth}
        type={headerType}
        id={id}
        className={className}
        color={color}
        dangerouslySetInnerHTML
        onClick={() => {
          scrollToId();
        }}
      >
        {copy}
      </Typography>
    </TitleContainer>
  );
};

export default BlogPostHeader;
